import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Video,
  Plus,
  Users,
  Calendar,
  LogOut,
  Settings,
  Shield,
  Link2,
  Film,
  Upload,
  Sparkles,
  Info,
  Crown,
  Loader2,
  Mail,
  Pencil,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react";
import { format } from "date-fns";
import { BetaBadge, BetaBanner } from "@/components/BetaBadge";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import RecordingManager from "@/components/RecordingManager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { z } from "zod";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import { getErrorMessage } from "@/lib/errorMessages";
import { hashPassword } from "@/lib/security";
import { Footer } from "@/components/Footer";
import { MeetingQRCode } from "@/components/MeetingQRCode";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import TemplatePickerModal from "@/components/TemplatePickerModal";
import { getTemplateById } from "@/config/templates";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  addRecentTemplate,
  clearDefaultTemplate,
  clearRecentTemplates,
  getDefaultTemplateId,
  getRecentTemplateIds,
  setDefaultTemplateId,
} from "@/utils/templatePrefs";
import { initializeEditorFromTemplate } from "@/lib/editorInitialization";
import { buildTemplateSeed } from "@/lib/templateUtils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateMeetingCode as generateCode, getMeetingLink } from "@/lib/meetingUtils";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { trackModalEvent } from "@/lib/monitoring/analytics";
import { EditProfileDialog } from "@/components/EditProfileDialog";

const meetingSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  description: z.string().max(1000, "Description must be less than 1000 characters").optional(),
  code: z.string().refine((code) => {
    const clean = code.replace(/-/g, '').toUpperCase();
    return /^[A-Z0-9]{9,12}$/.test(clean);
  }, "Invalid meeting code format"),
  password: z
    .string()
    .min(4, "Password must be at least 4 characters")
    .max(50, "Password must be under 50 characters")
    .optional(),
});

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  is_active: boolean;
  scheduled_start: string | null;
  scheduled_end: string | null;
  created_at: string;
}

interface Profile {
  display_name: string;
  avatar_url: string | null;
}

export default function Dashboard() {
  const { user, loading: authLoading, error: authError } = useAuth();
  const { isSuperAdmin, role } = useUserRole();
  const { flags } = useFeatureFlags();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [joinMeetingOpen, setJoinMeetingOpen] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [scheduledStart, setScheduledStart] = useState("");
  const [scheduledEnd, setScheduledEnd] = useState("");
  const [webinarScheduleEnabled, setWebinarScheduleEnabled] = useState(false);
  const [webinarStart, setWebinarStart] = useState("");
  const [webinarEmails, setWebinarEmails] = useState("");
  const [sendWebinarEmails, setSendWebinarEmails] = useState(true);
  const [meetingCode, setMeetingCode] = useState("");
  const [joinMeetingPassword, setJoinMeetingPassword] = useState("");
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(false);
  const [requirePasscode, setRequirePasscode] = useState(false);
  const [hostVideoEnabled, setHostVideoEnabled] = useState(true);
  const [participantVideoEnabled, setParticipantVideoEnabled] = useState(true);
  const [joinBeforeHost, setJoinBeforeHost] = useState(false);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateAction, setTemplateAction] = useState<"new" | "import" | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [recentTemplateIds, setRecentTemplateIds] = useState<string[]>([]);
  const [defaultTemplateId, setDefaultTemplateIdState] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState("");
  const [editorMetadata, setEditorMetadata] = useState<Record<string, string | number | boolean>>({});
  const [editorFields, setEditorFields] = useState<Record<string, string>>({});
  const [editorTitle, setEditorTitle] = useState("");
  const [editorTags, setEditorTags] = useState<string[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importContent, setImportContent] = useState("");
  const [importMetadata, setImportMetadata] = useState<Record<string, string | number | boolean>>({});
  const [lastTemplateAction, setLastTemplateAction] = useState<"new" | "import" | null>(null);
  const [activeCreateTab, setActiveCreateTab] = useState<"instant" | "schedule">("instant");
  const [generatedMeetingCode, setGeneratedMeetingCode] = useState("");
  const [generatedJoinLink, setGeneratedJoinLink] = useState("");
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    product_id: string | null;
    subscription_end: string | null;
  } | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [creationResult, setCreationResult] = useState<{
    title: string;
    code: string;
    link: string;
    passcode?: string | null;
    waitingRoom: boolean;
    requirePasscode: boolean;
    hostVideo: boolean;
    participantVideo: boolean;
    joinBeforeHost: boolean;
    recurrence?: string | null;
    scheduledStart?: string | null;
    scheduledEnd?: string | null;
  } | null>(null);
  const [recurrencePreset, setRecurrencePreset] = useState<string>("none");
  const navigate = useNavigate();
  const { toast } = useToast();
  const selectedTemplate = selectedTemplateId ? getTemplateById(selectedTemplateId) : null;
  const { addActivity } = useActivityFeed();
  const activeTemplateAction = templateAction ?? lastTemplateAction ?? "new";
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const shortcutHandledRef = useRef(false);
  const userContext = useMemo(() => ({ id: user?.id, email: user?.email, role }), [role, user?.email, user?.id]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/auth");
      } else {
        fetchProfile();
        fetchMeetings();
        fetchSubscription();
        setLoading(false);
      }
    }
  }, [user, authLoading, navigate]);

  // Auto-refresh subscription status every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchSubscription();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Handle subscription success redirect from Stripe checkout
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      toast({
        title: "Subscription Activated!",
        description: "Your subscription is now active. Enjoy your premium features!",
      });
      // Refresh subscription status
      fetchSubscription();
      // Clear the query param
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const authHeaders = await getSupabaseAuthHeaders();
      if (!authHeaders) {
        setSubscription(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: authHeaders,
      });
      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const getSubscriptionTier = () => {
    if (!subscription?.subscribed || !subscription.product_id) return 'free';
    const tiers: Record<string, string> = {
      'prod_TXw2zbIcWziifH': 'Starter',
      'prod_TXw218UdkpfROa': 'Professional',
      'prod_TXw2sEsDlC3ABl': 'Business',
    };
    return tiers[subscription.product_id] || 'Free';
  };


  useEffect(() => {
    const storedRecents = getRecentTemplateIds();
    const storedDefault = getDefaultTemplateId();

    setRecentTemplateIds(storedRecents);
    setDefaultTemplateIdState(storedDefault);

    if (storedDefault) {
      setSelectedTemplateId(storedDefault);
      const { content, metadata, fields, title, tags } = initializeEditorFromTemplate(storedDefault);
      setEditorContent(content);
      setEditorMetadata(metadata);
      setEditorFields(fields);
      setEditorTitle(title);
      setEditorTags(tags);
      setImportContent(content);
      setImportMetadata(metadata);
      setLastTemplateAction("new");
    }
  }, []);

  const getDefaultInstantTitle = () => `Instant meeting on ${format(new Date(), "PP")}`;

  const refreshGeneratedDetails = () => {
    const code = generateMeetingCode();
    setGeneratedMeetingCode(code);
    setGeneratedJoinLink(`${window.location.origin}/join/${code}`);
  };

  useEffect(() => {
    if (createMeetingOpen) {
      refreshGeneratedDetails();
      if (activeCreateTab === "instant") {
        setMeetingTitle(getDefaultInstantTitle());
      }
    } else {
      setCreationResult(null);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      setScheduledStart("");
      setScheduledEnd("");
      setRecurrencePreset("none");
      setRequirePasscode(false);
      setWaitingRoomEnabled(false);
      setHostVideoEnabled(true);
      setParticipantVideoEnabled(true);
      setJoinBeforeHost(false);
      setActiveCreateTab("instant");
    }
  }, [createMeetingOpen, activeCreateTab]);

  const handleDefaultChange = (templateId: string | null) => {
    if (templateId === null) {
      clearDefaultTemplate();
    } else {
      setDefaultTemplateId(templateId);
    }

    setDefaultTemplateIdState(templateId);
    setSelectedTemplateId(templateId);
  };

  const handleTemplateSelect = (
    templateId: string,
    options?: { setAsDefault?: boolean; actionOverride?: "new" | "import" | null },
  ) => {
    setSelectedTemplateId(templateId);
    const updatedRecents = addRecentTemplate(templateId);
    setRecentTemplateIds(updatedRecents);

    if (options?.setAsDefault) {
      handleDefaultChange(templateId);
    }

    const actionToUse = options?.actionOverride ?? templateAction;
    setLastTemplateAction(actionToUse);
    if (actionToUse) {
      setTemplateAction(actionToUse);
    }

    const template = getTemplateById(templateId);
    const actionLabel = actionToUse === "import" ? "Import" : "New";

    const { content, metadata, fields, title, tags } = initializeEditorFromTemplate(templateId);
    setEditorContent(content);
    setEditorMetadata(metadata);
    setEditorFields(fields);
    setEditorTitle(title);
    setEditorTags(tags);
    setImportContent(content);
    setImportMetadata(metadata);

    toast({
      title: `${actionLabel} template selected`,
      description: template
        ? `${template.name} is ready to use for your next document.`
        : `Template ${templateId} selected.`,
    });

    handleTemplatePickerToggle(false);

    if (actionToUse === "import") {
      setImportDialogOpen(true);
    }
  };

  const handleClearRecents = () => {
    clearRecentTemplates();
    setRecentTemplateIds([]);
  };

  const openTemplatePicker = (action: "new" | "import") => {
    setTemplateAction(action);
    setTemplatePickerOpen(true);
  };

  const handleCreateTabChange = (value: string) => {
    const tabValue = value === "schedule" ? "schedule" : "instant";
    setActiveCreateTab(tabValue);
    if (tabValue === "instant") {
      setMeetingTitle(getDefaultInstantTitle());
      refreshGeneratedDetails();
    }
  };

  const handleTemplatePickerToggle = (open: boolean) => {
    setTemplatePickerOpen(open);
    if (!open) {
      setTemplateAction(null);
    }
  };

  useEffect(() => {
    if (shortcutHandledRef.current) return;
    if (searchParams.get("newNote") === "1") {
      shortcutHandledRef.current = true;
      setTemplateAction("new");
      setTemplatePickerOpen(true);

      const next = new URLSearchParams(searchParams);
      next.delete("newNote");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleImportDocument = () => {
    if (!importContent.trim()) {
      toast({
        title: "Import content required",
        description: "Add content to import or paste your transcript before saving.",
        variant: "destructive",
      });
      return;
    }

    setEditorContent(importContent);
    setEditorMetadata(importMetadata);
    setImportDialogOpen(false);

    toast({
      title: "Import prepared",
      description: "Your imported document now includes the selected template metadata.",
    });
  };

  const fetchProfile = async () => {
    if (!user) return;

    const { data: profileData } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .single();

    if (profileData) {
      setProfile(profileData);
    }
  };

  const fetchMeetings = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("meetings")
      .select("id, title, description, meeting_code, is_active, scheduled_start, scheduled_end, created_at")
      .eq("host_id", user.id)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching meetings:", error);
      return;
    }

    setMeetings(data || []);
  };

  const generateMeetingCode = () => {
    return generateCode();
  };

  const createMeeting = async () => {
    if (!user || !meetingTitle.trim()) return;

    try {
      const code = generatedMeetingCode || generateMeetingCode();
      const joinLink = generatedJoinLink || `${window.location.origin}/join/${code}`;
      const passwordToUse = requirePasscode && !meetingPassword ? generateMeetingCode().slice(0, 6) : meetingPassword;

      const validation = meetingSchema.safeParse({
        title: meetingTitle,
        description: meetingDescription,
        code: code,
        password: passwordToUse ? passwordToUse : undefined,
      });

      if (!validation.success) {
        toast({
          title: "Validation Error",
          description: validation.error.errors[0].message,
          variant: "destructive",
        });
        return;
      }

      if (activeCreateTab === "schedule") {
        if (!scheduledStart || !scheduledEnd) {
          toast({
            title: "Schedule required",
            description: "Please provide both a start and end time for the scheduled meeting.",
            variant: "destructive",
          });
          return;
        }

        const startDate = new Date(scheduledStart);
        const endDate = new Date(scheduledEnd);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate <= startDate) {
          toast({
            title: "Invalid schedule",
            description: "End time must be after the start time for scheduled meetings.",
            variant: "destructive",
          });
          return;
        }
      }

      if (webinarScheduleEnabled && !webinarStart) {
        toast({
          title: "Webinar start time required",
          description: "Please select when webinar mode should turn on automatically.",
          variant: "destructive",
        });
        return;
      }

      const hashedPassword = passwordToUse ? await hashPassword(passwordToUse) : null;

      const { data, error } = await supabase
        .from("meetings")
        .insert({
          title: validation.data.title,
          description: validation.data.description || null,
          host_id: user.id,
          meeting_code: code,
          is_active: activeCreateTab === "instant",
          scheduled_start: activeCreateTab === "schedule" ? scheduledStart : null,
          scheduled_end: activeCreateTab === "schedule" ? scheduledEnd : null,
          waiting_room_enabled: waitingRoomEnabled,
          password: hashedPassword,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Meeting created successfully!",
        description: `Meeting code: ${code}`,
      });

        addActivity({
          action: "created",
          actor: { id: user.id, name: profile?.display_name || user.email },
          target: { title: `meeting "${validation.data.title}"` },
        });

      setCreationResult({
        title: validation.data.title,
        code,
        link: joinLink,
        passcode: passwordToUse || null,
        waitingRoom: waitingRoomEnabled,
        requirePasscode,
        hostVideo: hostVideoEnabled,
        participantVideo: participantVideoEnabled,
        joinBeforeHost,
        recurrence: recurrencePreset !== "none" ? recurrencePreset : null,
        scheduledStart: activeCreateTab === "schedule" ? scheduledStart : null,
        scheduledEnd: activeCreateTab === "schedule" ? scheduledEnd : null,
      });

      setMeetingTitle(activeCreateTab === "instant" ? getDefaultInstantTitle() : "");
      setMeetingDescription("");
      setMeetingPassword(passwordToUse || "");
      setScheduledStart("");
      setScheduledEnd("");
      setRecurrencePreset("none");
      setWebinarScheduleEnabled(false);
      setWebinarStart("");
      setWebinarEmails("");
      setSendWebinarEmails(true);
      setWaitingRoomEnabled(false);
      setRequirePasscode(false);
      setHostVideoEnabled(true);
      setParticipantVideoEnabled(true);
      setJoinBeforeHost(false);
      refreshGeneratedDetails();
      fetchMeetings();
    } catch (error: any) {
      const isRateLimit = handleRateLimitError(error, "meeting_create");

      if (!isRateLimit) {
        const { title, description } = getErrorMessage(error);
        toast({
          title,
          description,
          variant: "destructive",
        });
      }
    }
  };

  const getInviteDetails = () => {
    return {
      title: creationResult?.title || meetingTitle || "Meeting",
      link: creationResult?.link || generatedJoinLink,
      passcode: creationResult?.passcode || meetingPassword || null,
    };
  };

  const handleCopyValue = async (value: string, title: string, description?: string) => {
    await navigator.clipboard.writeText(value);
    toast({ title, description });
  };

  const handleCopyInvite = () => {
    const { title, link, passcode } = getInviteDetails();
    const invite = `${title}\nJoin: ${link}${passcode ? `\nPasscode: ${passcode}` : ""}`;
    handleCopyValue(invite, "Invite copied!", "Send this link to participants");
  };

  const getCalendarTimes = () => {
    const start = creationResult?.scheduledStart || scheduledStart || new Date().toISOString();
    const end = creationResult?.scheduledEnd || scheduledEnd;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date(startDate.getTime() + 30 * 60 * 1000);

    return { startDate, endDate };
  };

  const openCalendarLink = (provider: "google" | "outlook") => {
    const { title, link, passcode } = getInviteDetails();
    const { startDate, endDate } = getCalendarTimes();
    const start = format(startDate, "yyyyMMdd'T'HHmmss'Z'");
    const end = format(endDate, "yyyyMMdd'T'HHmmss'Z'");
    const details = `${link}${passcode ? `\nPasscode: ${passcode}` : ""}`;

    if (provider === "google") {
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(link)}`;
      window.open(googleUrl, "_blank");
      return;
    }

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(title)}&body=${encodeURIComponent(details)}&startdt=${encodeURIComponent(startDate.toISOString())}&enddt=${encodeURIComponent(endDate.toISOString())}`;
    window.open(outlookUrl, "_blank");
  };

  const downloadIcs = () => {
    const { title, link, passcode } = getInviteDetails();
    const { startDate, endDate } = getCalendarTimes();
    const start = format(startDate, "yyyyMMdd'T'HHmmss'Z'");
    const end = format(endDate, "yyyyMMdd'T'HHmmss'Z'");
    const description = `${link}${passcode ? `\\nPasscode: ${passcode}` : ""}`;

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//MeetHub//EN",
      "CALSCALE:GREGORIAN",
      "BEGIN:VEVENT",
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${link}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${title.replace(/\s+/g, "-").toLowerCase()}.ics`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const renderCreateForm = (isScheduled: boolean) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Meeting Title</Label>
        <Input
          id="title"
          placeholder="Enter meeting title"
          value={meetingTitle}
          onChange={(e) => setMeetingTitle(e.target.value)}
          maxLength={200}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Input
          id="description"
          placeholder="Meeting description"
          value={meetingDescription}
          onChange={(e) => setMeetingDescription(e.target.value)}
          maxLength={1000}
        />
      </div>

      {isScheduled && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start">Start Date & Time</Label>
              <Input
                id="start"
                type="datetime-local"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">End Date & Time</Label>
              <Input
                id="end"
                type="datetime-local"
                value={scheduledEnd}
                onChange={(e) => setScheduledEnd(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="recurrence">Recurrence</Label>
            <select
              id="recurrence"
              value={recurrencePreset}
              onChange={(e) => setRecurrencePreset(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="none">Does not repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Pick a preset to add the meeting to calendars with the correct cadence.
            </p>
          </div>
        </div>
      )}

      {/* Visual Meeting Code Preview */}
      <div className="p-4 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">Your Meeting Code</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshGeneratedDetails}
            className="h-8 px-2 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            New Code
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-background rounded-lg p-3 border">
            <p className="text-xl sm:text-2xl font-mono font-bold text-center tracking-wider text-foreground">
              {generatedMeetingCode}
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleCopyValue(generatedJoinLink, "Meeting link copied", "Share this link to invite others")}
            className="shrink-0 h-12 w-12"
            title="Copy meeting link"
          >
            <Copy className="h-5 w-5" />
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground text-center truncate">
          {generatedJoinLink}
        </p>
        
        <Button variant="secondary" className="w-full" size="sm" onClick={handleCopyInvite}>
          <Mail className="h-4 w-4 mr-2" />
          Copy Full Invite
        </Button>
      </div>

      <div className="space-y-4 rounded-lg border border-border p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="flex items-center gap-2">Waiting Room</Label>
            <p className="text-sm text-muted-foreground">Keep participants queued until you admit them.</p>
          </div>
          <Switch checked={waitingRoomEnabled} onCheckedChange={setWaitingRoomEnabled} />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="flex items-center gap-2">Require passcode</Label>
            <p className="text-sm text-muted-foreground">Enforce entry with a passcode for added security.</p>
          </div>
          <Switch checked={requirePasscode} onCheckedChange={setRequirePasscode} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Meeting Passcode</Label>
          <PasswordInput
            id="password"
            value={meetingPassword}
            onChange={(e) => setMeetingPassword(e.target.value)}
            placeholder="Set a password for extra security"
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground">
            Participants must enter this password when joining. Leave blank to generate an auto-passcode when required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Host video</Label>
              <p className="text-sm text-muted-foreground">Start meetings with host video enabled.</p>
            </div>
            <Switch checked={hostVideoEnabled} onCheckedChange={setHostVideoEnabled} />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Participant video</Label>
              <p className="text-sm text-muted-foreground">Let participants join with video on by default.</p>
            </div>
            <Switch checked={participantVideoEnabled} onCheckedChange={setParticipantVideoEnabled} />
          </div>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <Label>Join before host</Label>
            <p className="text-sm text-muted-foreground">Allow attendees to join before you arrive.</p>
          </div>
          <Switch checked={joinBeforeHost} onCheckedChange={setJoinBeforeHost} />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <Switch id="webinar-mode" checked={webinarScheduleEnabled} onCheckedChange={setWebinarScheduleEnabled} />
            <div>
              <Label htmlFor="webinar-mode">Schedule webinar mode</Label>
              <p className="text-sm text-muted-foreground">Automatically enable webinar mode and notify attendees.</p>
            </div>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label="Learn about webinar scheduling"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm max-w-xs">
                Keep attendees informed and start meetings in webinar mode with a single toggle.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {webinarScheduleEnabled && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="webinar-start">Webinar start time</Label>
                <Input
                  id="webinar-start"
                  type="datetime-local"
                  value={webinarStart}
                  onChange={(e) => setWebinarStart(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="webinar-emails">Notify participants (comma separated)</Label>
                <Textarea
                  id="webinar-emails"
                  placeholder="name@example.com, colleague@example.com"
                  value={webinarEmails}
                  onChange={(e) => setWebinarEmails(e.target.value)}
                  className="min-h-[60px]"
                />
                <div className="flex items-center gap-2">
                  <Switch id="send-webinar-emails" checked={sendWebinarEmails} onCheckedChange={setSendWebinarEmails} />
                  <Label htmlFor="send-webinar-emails" className="text-sm">
                    Send email notifications
                  </Label>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              We will enable webinar mode automatically at the selected time and notify attendees with the join link.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 justify-between">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openCalendarLink("google")}>
            Google Calendar
          </Button>
          <Button variant="outline" onClick={() => openCalendarLink("outlook")}>
            Outlook
          </Button>
          <Button variant="outline" onClick={downloadIcs}>
            ICS
          </Button>
        </div>
        <Button
          onClick={createMeeting}
          className="min-w-[180px]"
          disabled={!meetingTitle.trim() || (isScheduled && (!scheduledStart || !scheduledEnd))}
        >
          {isScheduled ? "Schedule meeting" : "Create meeting"}
        </Button>
      </div>
    </div>
  );

  const joinMeeting = async () => {
    if (!meetingCode.trim()) return;

    try {
      // Use the secure function to verify meeting password
      const { data, error } = await supabase
        .rpc("verify_meeting_password", {
          _meeting_code: meetingCode.toUpperCase(),
          _password: joinMeetingPassword || null,
        })
        .single();

      if (error || !data || !data.is_valid) {
        toast({
          title: "Meeting not found",
          description: joinMeetingPassword
            ? "Please check the meeting code or password and try again."
            : "Please check the meeting code and try again.",
          variant: "destructive",
        });
        return;
      }

      setJoinMeetingOpen(false);
      setJoinMeetingPassword("");
      setMeetingCode("");
      navigate(`/meeting/${data.meeting_id}`);
    } catch (error: any) {
      const { title, description } = getErrorMessage(error);
      toast({
        title,
        description,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (authError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-destructive">Connection Error</CardTitle>
            <CardDescription>Unable to connect to the authentication service.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{authError}</p>
            <p className="text-sm text-muted-foreground">
              This may be due to network issues or domain configuration. Please try:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Checking your internet connection</li>
              <li>Accessing via the staging URL</li>
              <li>Verifying custom domain DNS settings</li>
            </ul>
            <Button onClick={() => window.location.reload()} className="w-full">
              Retry Connection
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Video className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Video className="h-8 w-8 text-primary" />
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">MeetHub</h1>
              <BetaBadge />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="hidden md:inline text-sm text-muted-foreground">
              Welcome, {profile?.display_name || user?.email}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="hidden md:inline-flex items-center gap-2"
              onClick={() => setProfileDialogOpen(true)}
            >
              <Pencil className="h-4 w-4" />
              Edit profile
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Account
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="line-clamp-2">{profile?.display_name || user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isSuperAdmin && (
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault();
                      navigate("/admin");
                    }}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setProfileDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    navigate("/profile");
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setSignOutDialogOpen(true);
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog open={signOutDialogOpen} onOpenChange={setSignOutDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will need to sign in again to access your dashboard.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSignOut}>Logout</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Email Verification Banner */}
        {user && !user.email_confirmed_at && user.email && (
          <EmailVerificationBanner email={user.email} />
        )}

        {/* Subscription Status Card */}
        <Card className="mb-6 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${subscription?.subscribed ? 'bg-primary/20' : 'bg-muted'}`}>
                  <Crown className={`h-5 w-5 ${subscription?.subscribed ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {subscriptionLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        `${getSubscriptionTier()} Plan`
                      )}
                    </span>
                    {subscription?.subscribed && (
                      <Badge variant="default" className="text-xs">Active</Badge>
                    )}
                  </div>
                  {subscription?.subscription_end && (
                    <p className="text-xs text-muted-foreground">
                      Renews {new Date(subscription.subscription_end).toLocaleDateString()}
                    </p>
                  )}
                  {!subscription?.subscribed && !subscriptionLoading && (
                    <p className="text-xs text-muted-foreground">
                      Upgrade to unlock premium features
                    </p>
                  )}
                </div>
              </div>
              <Button 
                variant={subscription?.subscribed ? "outline" : "default"} 
                size="sm"
                onClick={() => navigate("/pricing")}
              >
                {subscription?.subscribed ? "Manage Plan" : "Upgrade"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Beta Banner */}
        <BetaBanner />

        <Tabs defaultValue="meetings" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="meetings">
              <Calendar className="h-4 w-4 mr-2" />
              Meetings
            </TabsTrigger>
            <TabsTrigger value="recordings">
              <Film className="h-4 w-4 mr-2" />
              Recordings
            </TabsTrigger>
          </TabsList>

            <TabsContent value="meetings" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {selectedTemplateId && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-medium">{selectedTemplate?.name || selectedTemplateId}</span>
                  </Badge>
                  {lastTemplateAction && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {lastTemplateAction} action active
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {selectedTemplateId && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Template starter
                    </CardTitle>
                    <CardDescription>Editor content and metadata seeded from your template selection.</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      {getTemplateById(selectedTemplateId)?.name}
                    </span>
                    <span className="rounded-full bg-muted px-3 py-1">
                      {activeTemplateAction === "import" ? "Import" : "New"} flow
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-[2fr,1fr]">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm font-medium text-foreground">
                        <span>Initial editor content</span>
                        <span className="text-xs text-muted-foreground">Placeholders auto-filled</span>
                      </div>
                      <ScrollArea className="h-40 rounded-md border border-border bg-background/70">
                        <pre className="whitespace-pre-wrap p-3 text-sm leading-relaxed text-foreground">
                          {editorContent || "Template has no default content."}
                        </pre>
                      </ScrollArea>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
                        <div className="flex items-center justify-between text-sm font-medium text-foreground">
                          <span>Default metadata</span>
                          <span className="text-xs text-muted-foreground">Applied automatically</span>
                        </div>
                        {Object.keys(editorMetadata).length === 0 ? (
                          <p className="text-sm text-muted-foreground">No metadata included for this template.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(editorMetadata).map(([key, value]) => (
                              <span
                                key={key}
                                className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground border border-border"
                              >
                                {key}: {String(value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
                        <div className="flex items-center justify-between text-sm font-medium text-foreground">
                          <span>Document basics</span>
                          <span className="text-xs text-muted-foreground">Title & tags</span>
                        </div>
                        <div className="space-y-1 text-sm text-foreground">
                          <p className="font-semibold">{editorTitle || "Title will mirror the template name."}</p>
                          <div className="flex flex-wrap gap-2">
                            {editorTags.length === 0 ? (
                              <span className="text-xs text-muted-foreground">No tags provided by this template.</span>
                            ) : (
                              editorTags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="rounded-full text-xs">
                                  {tag}
                                </Badge>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
                        <div className="flex items-center justify-between text-sm font-medium text-foreground">
                          <span>Form defaults</span>
                          <span className="text-xs text-muted-foreground">
                            {Object.keys(editorFields).length} fields
                          </span>
                        </div>
                        {Object.keys(editorFields).length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No structured fields are defined for this template.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(editorFields).map(([key, value]) => (
                              <span
                                key={key}
                                className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground border border-border"
                              >
                                {key}: {value || "Not provided"}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dialog open={createMeetingOpen} onOpenChange={setCreateMeetingOpen}>
                <DialogTrigger asChild>
                  <Card
                    className="hover:shadow-lg transition-shadow cursor-pointer border-primary/20 hover:border-primary/40"
                    data-tour="create-session"
                  >
                    <CardContent className="flex items-center space-x-4 p-6">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Plus className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Create Meeting</h3>
                        <p className="text-sm text-muted-foreground">Start a new video conference</p>
                      </div>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                  <DialogHeader className="space-y-2 sm:space-y-3">
                    <DialogTitle className="text-lg sm:text-xl">Create New Meeting</DialogTitle>
                    <DialogDescription className="text-sm sm:text-base">Set up a new video conference meeting.</DialogDescription>
                  </DialogHeader>
                  <Tabs value={activeCreateTab} onValueChange={handleCreateTabChange} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="instant">New Meeting</TabsTrigger>
                      <TabsTrigger value="schedule">Schedule</TabsTrigger>
                    </TabsList>
                    <TabsContent value="instant" className="space-y-4">
                      {renderCreateForm(false)}
                    </TabsContent>
                    <TabsContent value="schedule" className="space-y-4">
                      {renderCreateForm(true)}
                    </TabsContent>
                  </Tabs>

                  {creationResult && (
                    <Card className="mt-4 border-primary/30 bg-primary/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Meeting ready to share
                        </CardTitle>
                        <CardDescription>Send the details below to your participants.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Title</p>
                            <p className="font-semibold">{creationResult.title}</p>
                            {creationResult.recurrence && (
                              <Badge variant="outline" className="w-fit">
                                {creationResult.recurrence} recurrence
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Meeting code</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="font-mono text-sm py-1 px-2">
                                {creationResult.code}
                              </Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCopyValue(creationResult.code, "Meeting code copied")}
                              >
                                Copy
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Join link</p>
                            <div className="flex flex-col gap-2">
                              <span className="font-mono text-sm break-all">{creationResult.link}</span>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleCopyValue(
                                      creationResult.link,
                                      "Link copied",
                                      "Share this link to invite others",
                                    )
                                  }
                                >
                                  Copy link
                                </Button>
                                <Button size="sm" variant="secondary" onClick={handleCopyInvite}>
                                  Copy invite
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Passcode</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{creationResult.passcode || "None"}</Badge>
                              {creationResult.passcode && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCopyValue(creationResult.passcode!, "Passcode copied")}
                                >
                                  Copy
                                </Button>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Waiting room {creationResult.waitingRoom ? "enabled" : "disabled"} • Passcode{" "}
                              {creationResult.requirePasscode ? "required" : "optional"}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <p className="text-sm font-semibold">Host defaults</p>
                            <ul className="text-sm text-muted-foreground space-y-1">
                              <li>Host video: {creationResult.hostVideo ? "On" : "Off"}</li>
                              <li>Participant video: {creationResult.participantVideo ? "On" : "Off"}</li>
                              <li>Join before host: {creationResult.joinBeforeHost ? "Allowed" : "Disabled"}</li>
                            </ul>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-semibold">Share</p>
                            <div className="flex flex-wrap gap-2">
                              <Button variant="outline" size="sm" onClick={() => openCalendarLink("google")}>
                                Google Calendar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openCalendarLink("outlook")}>
                                Outlook
                              </Button>
                              <Button variant="outline" size="sm" onClick={downloadIcs}>
                                ICS
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={joinMeetingOpen} onOpenChange={setJoinMeetingOpen}>
                <DialogTrigger asChild>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer border-stream-secondary/20 hover:border-stream-secondary/40">
                    <CardContent className="flex items-center space-x-4 p-6">
                      <div className="p-3 bg-stream-secondary/10 rounded-lg">
                        <Users className="h-6 w-6 text-stream-secondary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Join Meeting</h3>
                        <p className="text-sm text-muted-foreground">Enter a meeting with a code</p>
                      </div>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Join Meeting</DialogTitle>
                    <DialogDescription>Enter the meeting code to join an existing meeting.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Meeting Code</Label>
                      <Input
                        id="code"
                        placeholder="Enter meeting code"
                        value={meetingCode}
                        onChange={(e) => setMeetingCode(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="join-password">Password (if required)</Label>
                      <PasswordInput
                        id="join-password"
                        placeholder="Enter meeting password"
                        value={joinMeetingPassword}
                        onChange={(e) => setJoinMeetingPassword(e.target.value)}
                      />
                    </div>
                    <Button onClick={joinMeeting} className="w-full" disabled={!meetingCode.trim()}>
                      Join Meeting
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-6">
              {/* Recent Meetings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Recent Meetings
                  </CardTitle>
                  <CardDescription>Your recently created meetings</CardDescription>
                </CardHeader>
                <CardContent>
                  {meetings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No meetings yet. Create your first meeting to get started!</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {meetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-1">
                            <h4 className="font-medium">{meeting.title}</h4>
                            <p className="text-sm text-muted-foreground">Code: {meeting.meeting_code}</p>
                            {meeting.scheduled_end && (
                              <p className="text-xs text-muted-foreground mt-1">
                                End: {format(new Date(meeting.scheduled_end), "PPp")}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const link = `${window.location.origin}/join/${meeting.meeting_code}`;
                                navigator.clipboard.writeText(link);
                                toast({
                                  title: "Link copied!",
                                  description: "Share this link to invite others",
                                });
                              }}
                              title="Copy meeting link"
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const link = `${window.location.origin}/join/${meeting.meeting_code}`;
                                const subject = encodeURIComponent(`Join my meeting: ${meeting.title}`);
                                const body = encodeURIComponent(
                                  `You're invited to join my meeting!\n\n` +
                                  `Meeting: ${meeting.title}\n` +
                                  `Meeting Code: ${meeting.meeting_code}\n` +
                                  (meeting.scheduled_start ? `Scheduled: ${format(new Date(meeting.scheduled_start), "PPp")}\n` : '') +
                                  `\nJoin here: ${link}`
                                );
                                window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                              }}
                              title="Share via email"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            {meeting.is_active && (
                              <>
                                <MeetingQRCode meetingCode={meeting.meeting_code} meetingTitle={meeting.title} />
                                <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                                  Active
                                </span>
                              </>
                            )}
                            <Button
                              size="sm"
                              onClick={() => navigate(`/meeting/${meeting.id}`)}
                              disabled={!meeting.is_active}
                            >
                              {meeting.is_active ? "Join" : "Ended"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          <TabsContent value="recordings">
            <RecordingManager showMeetingTitle={true} />
          </TabsContent>
        </Tabs>

        <TemplatePickerModal
          open={templatePickerOpen}
          onOpenChange={handleTemplatePickerToggle}
          onSelect={handleTemplateSelect}
          recentTemplateIds={recentTemplateIds}
          defaultTemplateId={defaultTemplateId}
          onSetDefault={handleDefaultChange}
          onClearRecents={handleClearRecents}
          action={templateAction}
        />

        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import from template</DialogTitle>
              <DialogDescription>
                Start from the template defaults, then paste or edit your text before importing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="import-content">Document content</Label>
                <Textarea
                  id="import-content"
                  value={importContent}
                  onChange={(event) => setImportContent(event.target.value)}
                  className="min-h-[160px]"
                />
                <p className="text-xs text-muted-foreground">
                  Template defaults are pre-populated. Paste your transcript or notes to merge with the selected
                  metadata.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Metadata to attach</Label>
                <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
                  {Object.keys(importMetadata).length === 0 ? (
                    <span className="text-sm text-muted-foreground">No metadata provided for this template.</span>
                  ) : (
                    Object.entries(importMetadata).map(([key, value]) => (
                      <span
                        key={key}
                        className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground border border-border"
                      >
                        {key}: {String(value)}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <Button onClick={handleImportDocument} className="w-full">
                Prepare import
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <EditProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />

      <Footer />
    </div>
  );
}
